// Also imported here as this module is used by entries other than the main website entry
import '../module/customer-storage.js'

const STORAGE_PREFIX = 'product-price-id-'
const DEFAULT_TTL = 300000

/*
 * Cache netwerkrequests gedurende de huidige paginalaad.
 * Hierdoor wordt dezelfde combinatie van product-ID's niet dubbel opgehaald.
 */
const requestCache = new Map()

function getStorageKey(identifier) {
  return `${STORAGE_PREFIX}${identifier}`
}

function storePrice(identifier, value, ttl = DEFAULT_TTL) {
  const item = {
    value,
    expiry: Date.now() + ttl,
  }

  try {
    localStorage.setItem(getStorageKey(identifier), JSON.stringify(item))
  } catch (error) {
    /*
     * localStorage kan bijvoorbeeld geblokkeerd zijn in private mode.
     * De prijzen blijven dan gewoon werken, alleen zonder persistente cache.
     */
    console.warn('Product price could not be cached.', error)
  }
}

function getPrice(identifier) {
  const storageKey = getStorageKey(identifier)

  let stored

  try {
    stored = localStorage.getItem(storageKey)
  } catch {
    return null
  }

  if (!stored) {
    return null
  }

  try {
    const item = JSON.parse(stored)

    if (!item || typeof item.expiry !== 'number' || item.value === undefined) {
      localStorage.removeItem(storageKey)

      return null
    }

    if (Date.now() > item.expiry) {
      localStorage.removeItem(storageKey)

      return null
    }

    return item.value
  } catch {
    localStorage.removeItem(storageKey)

    return null
  }
}

function updatePriceElement(element, data) {
  if (!element || !data) {
    return
  }

  const current = element.querySelector('[data-product-price-current]')
  const original = element.querySelector('[data-product-price-original]')
  const placeholder = element.querySelector('[data-product-price-placeholder]')

  if (!current || !original) {
    return
  }

  const price = data.price ?? ''
  const discountedPrice = data.price_discounted ?? price
  const hasDiscount = discountedPrice && price !== discountedPrice

  current.textContent = discountedPrice
  current.classList.toggle('text-danger', hasDiscount)
  current.classList.remove('d-none')

  if (hasDiscount) {
    original.textContent = price
    original.classList.remove('d-none')
  } else {
    original.textContent = ''
    original.classList.add('d-none')
  }

  placeholder?.remove()
}

function updateGiveawayElement(card, data) {
  const element = card.querySelector('[data-product-giveaway]')

  if (!element) {
    return
  }

  const marketingSentence = typeof data.giveaway_marketing_sentence === 'string' ? data.giveaway_marketing_sentence.trim() : ''

  element.textContent = marketingSentence
  element.classList.toggle('d-none', !marketingSentence)
}

function updatePriceElements(root, identifier, data) {
  const elements = root.querySelectorAll(`[data-product-price-id="${identifier}"]`)

  elements.forEach((element) => {
    updatePriceElement(element, data)

    const card = element.closest('.product-card')

    if (card) {
      updateGiveawayElement(card, data)
    }
  })

  updateDiscountBadge(root, identifier, data)
}

async function fetchPrices(endpoint, identifiers) {
  const response = await fetch(endpoint, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      identifiers,
    }),
  })

  if (!response.ok) {
    throw new Error(`Price request failed with status ${response.status}.`)
  }

  return response.json()
}

function fetchPricesCached(endpoint, identifiers) {
  const cacheKey = [endpoint, ...identifiers].join(':')

  if (requestCache.has(cacheKey)) {
    return requestCache.get(cacheKey)
  }

  const request = fetchPrices(endpoint, identifiers).catch((error) => {
    /*
     * Een mislukte request mag een volgende poging niet blokkeren.
     */
    requestCache.delete(cacheKey)

    throw error
  })

  requestCache.set(cacheKey, request)

  return request
}

function normalizeIdentifiers(identifiers) {
  return [...new Set(identifiers.map(Number).filter(Number.isInteger))].sort((a, b) => a - b)
}

export async function resolvePrices({ endpoint, root, identifiers, ttl = DEFAULT_TTL }) {
  if (!endpoint) {
    throw new Error('A price endpoint is required.')
  }

  if (!(root instanceof Element)) {
    throw new TypeError('A valid price resolver root element is required.')
  }

  const normalizedIdentifiers = normalizeIdentifiers(identifiers)

  if (normalizedIdentifiers.length === 0) {
    return
  }

  const unresolvedIdentifiers = []

  normalizedIdentifiers.forEach((identifier) => {
    const cachedPrice = getPrice(identifier)

    if (cachedPrice) {
      updatePriceElements(root, identifier, cachedPrice)

      return
    }

    unresolvedIdentifiers.push(identifier)
  })

  if (unresolvedIdentifiers.length === 0) {
    return
  }

  const response = await fetchPricesCached(endpoint, unresolvedIdentifiers)

  Object.values(response ?? {}).forEach((resolved) => {
    const identifier = Number(resolved?.id)
    const data = resolved?.data

    if (!Number.isInteger(identifier) || !data) {
      return
    }

    storePrice(identifier, data, ttl)

    updatePriceElements(root, identifier, data)
  })
}

export function clearPrices() {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(STORAGE_PREFIX))
      .forEach((key) => {
        localStorage.removeItem(key)
      })
  } catch (error) {
    console.warn('Product price cache could not be cleared.', error)
  }

  requestCache.clear()
}

function updateDiscountBadge(root, identifier, data) {
  const elements = root.querySelectorAll(`[data-product-discount-id="${identifier}"]`)

  elements.forEach((element) => {
    const discountPercentage = Number(data.discount_percentage)

    if (!Number.isFinite(discountPercentage) || discountPercentage <= 0) {
      element.classList.add('d-none')

      return
    }

    element.textContent = `-${discountPercentage}%`
    element.classList.remove('d-none')
  })
}

window.addEventListener('vat-toggle', clearPrices, {
  passive: true,
})
